import { Log, UserManager as OIDCUserManager, User } from 'oidc-client-ts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';

import { isISOCountryCode, parseCountryCode } from '@foundation/countries';

import type { AuthContextValues } from './AuthContextValues';
import type { SignInState } from './SignInState';
import type { SignOutState } from './SignOutState';

const AuthContext = createContext<AuthContextValues | undefined>(undefined);

type AuthProviderProps = PropsWithChildren<{
  readonly userManager: OIDCUserManager;
}>;

export function AuthProvider({ children, userManager }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Initialize UserManager
  useEffect(() => {
    Log.setLogger(console);
    Log.setLevel(Log.WARN);

    return () => {
      userManager.stopSilentRenew();
      userManager.clearStaleState();
    };
  }, [userManager]);

  // Setup event listeners and load user
  useEffect(() => {
    const handleUserLoaded = (loadedUser: User) => {
      console.log('User loaded/refreshed:', loadedUser.profile);
      setUser(loadedUser);
    };

    const handleUserUnloaded = () => {
      console.log('User signed out');
      setUser(null);
    };

    const handleAccessTokenExpiring = () => {
      console.log('Access token expiring, silent renew should occur');
    };

    const handleAccessTokenExpired = () => {
      console.log('Access token expired');
      setUser(null);
    };

    const handleSilentRenewError = (error: Error) => {
      console.error('Silent renew error:', error);
      // Could trigger re-authentication here
    };

    // Attach event listeners
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addUserSignedOut(handleUserUnloaded);
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    // Cleanup
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeUserSignedOut(handleUserUnloaded);
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [userManager]);

  const signIn = useCallback(
    async (currentState?: SignInState) => {
      const state = currentState ? JSON.stringify(currentState) : undefined;
      await userManager.signinRedirect({
        state,
        redirectMethod: 'replace',
      });
    },
    [userManager],
  );

  const signOut = useCallback(
    async (currentState?: SignOutState) => {
      // Mark the sign-out intent BEFORE calling signoutRedirect.
      // oidc-client-ts fires `userUnloaded` synchronously inside signoutRedirect,
      // which would otherwise let guarded routes (RequireAuth) trigger a new
      // sign-in redirect and cancel the in-flight endsession navigation.
      setIsSigningOut(true);
      try {
        await userManager.signoutRedirect({
          state: currentState ? JSON.stringify(currentState) : undefined,
        });
      } catch (error) {
        setIsSigningOut(false);
        throw error;
      }
    },
    [userManager],
  );

  const finishSignIn = useCallback(async (): Promise<
    SignInState | undefined
  > => {
    const signedInUser = await userManager.signinCallback();

    if (signedInUser && !signedInUser.expired) {
      setUser(signedInUser);

      let locationState: SignInState | undefined;

      if (signedInUser.state && typeof signedInUser.state === 'string') {
        try {
          locationState = JSON.parse(signedInUser.state) as SignInState;
        } catch {
          locationState = undefined;
        }
      }

      return locationState;
    }

    throw new Error('Sign-in failed');
  }, [userManager]);

  const finishSignOut = useCallback(async (): Promise<
    SignOutState | undefined
  > => {
    const { userState } = await userManager.signoutRedirectCallback();

    if (typeof userState === 'string') {
      try {
        return JSON.parse(userState) as SignOutState;
      } catch {
        return undefined;
      }
    }

    return undefined;
  }, [userManager]);

  const isCustomer = (() => {
    const isCustomerValue = user?.profile?.is_customer;

    return (
      (typeof isCustomerValue === 'boolean' && isCustomerValue) ||
      (typeof isCustomerValue === 'string' &&
        isCustomerValue.toLowerCase() === 'true')
    );
  })();

  const isPaymentsPreviewUser = (
    (user?.profile['role'] as string) ?? ''
  ).includes('payments_preview_user');

  const givenName = (() => {
    const givenNameValue = user?.profile?.given_name;
    return typeof givenNameValue === 'string' ? givenNameValue : undefined;
  })();

  const familyName = (() => {
    const familyNameValue = user?.profile?.family_name;
    return typeof familyNameValue === 'string' ? familyNameValue : undefined;
  })();

  const isImpersonator = (() => {
    try {
      const act = JSON.parse(user?.profile?.act as string).sub ?? undefined;
      return act !== undefined;
    } catch {
      return false;
    }
  })();

  const countryCode = (() => {
    const countryCodeValue = user?.profile?.address?.country;

    if (countryCodeValue && isISOCountryCode(countryCodeValue as string)) {
      return parseCountryCode(countryCodeValue as string);
    }

    return undefined;
  })();

  const value: AuthContextValues = {
    isAuthenticated: user !== null && !user.expired,
    isSigningOut,
    accessToken: user?.access_token,
    userId: user?.profile.sub,
    isPaymentsPreviewUser,
    isCustomer,
    isTeamPartner: !isCustomer,
    countryCode,
    givenName,
    familyName,
    isImpersonator,
    signIn,
    signOut,
    finishSignIn,
    finishSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValues {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
